/* ez-pdp-buy-hero.js — variant/option sync, ADD-ON rows, live line-item total, and a multi-line
   add-to-cart for the PDP buy hero (contract §1 split, §9.7 authorized).
   INSTANCE-SAFETY: this file iterates EVERY `.ez-pdp` root on the page and scopes all queries to
   that root, so two instances on one page never cross-wire. Each root carries its section id in
   `data-section-id`.

   Behaviour parity with the demo PrecutPDP/BandPDP/RollPDP buy panels:
     • variant option pickers (cards / swatches) keep the hidden variant <select> in sync;
     • ADD-ON rows (body-color studs, storage bag, …) are checkbox toggles that reference a
       secondary product variant (id + bound price baked into the row markup by Liquid). A stud
       row can carry a colour sub-picker whose swatch flips the row to the same-colour variant.
     • a live line-item SUMMARY + TOTAL recomputes from the kit variant + every checked add-on;
     • add-to-cart POSTs `/cart/add.js` with `items:[kit, …checked add-ons]` (the same shape the
       configurator islands use), then dispatches `ez:cart:added` (opens the shared drawer).
       Falls back to a hard /cart redirect when the section is set to skip the drawer, or on error. */
(function () {
  function money(cents) {
    // format like Shopify's money filter (no currency subunits when whole). cents is an integer.
    var n = (Number(cents) || 0) / 100;
    // non-store currency (Canada market = CAD): the ONE theme formatter, window.EZ (layout/theme.liquid)
    var E = window.EZ;
    if (E && E.isBase === false && typeof E.money === 'function') return E.money(n);
    var s = n.toLocaleString('en-US', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
    return '$' + s;
  }

  function initInstance(root) {
    if (!root || root.dataset.ezPdpInit === '1') return;
    root.dataset.ezPdpInit = '1';

    var sectionId = root.getAttribute('data-section-id') || '';
    var sel = root.querySelector('[data-ez-variant-select]');
    var optInputs = root.querySelectorAll('[data-ez-opt-input]');
    var priceEl = root.querySelector('[data-ez-price]');
    var atc = root.querySelector('[data-ez-atc]');
    var atcText = root.querySelector('[data-ez-atc-text]');
    if (!sel) return;

    var optGroups = [];
    root.querySelectorAll('[data-ez-opt-index]').forEach(function (o) { optGroups.push(o); });

    var addonRows = Array.prototype.slice.call(root.querySelectorAll('[data-ez-addon]'));

    // ── stud color (2026-10-07): inline choice after the material color on skirting kits. It follows the
    //    material color until the shopper picks a different one; a different stud color = flat $60 per kit build.
    var studGroup = root.querySelector('[data-ez-stud-group]');
    var studNote = studGroup && root.querySelector('[data-ez-stud-note]');
    // the "What's in the box" snaps line: names the cap + stud colors once the fabric color is known
    var boxSnaps = studGroup && root.querySelector('[data-ez-box-snaps] .ez-pdp__box-text');
    var boxSnapsOrig = boxSnaps ? boxSnaps.textContent : '';
    var boxSnapsN = boxSnaps ? boxSnaps.parentNode.getAttribute('data-ez-box-snaps') : '';
    var studFollows = true;
    var STUD_UPGRADE_CENTS = Math.round(6000 * ((window.Shopify && window.Shopify.currency && +window.Shopify.currency.rate) || 1));
    // the color option group (swatches); the stud default exists only once it has a pick
    var colorGroup = optGroups.filter(function (g) { return g.querySelector('.ez-pdp__swatch [data-ez-opt-input]'); })[0] || null;
    function kitCap() {
      if (colorGroup && !colorGroup.querySelector('[data-ez-opt-input]:checked')) return '';
      var k = currentKit(); return k && k.sku ? k.sku.split('-').pop() : '';
    }
    function studPick() {
      if (!studGroup) return null;
      var c = studGroup.querySelector('[data-ez-stud-input]:checked');
      return c ? { code: c.value, name: c.getAttribute('data-color-name') || c.value } : null;
    }
    function syncStud() {
      if (!studGroup) return;
      var cap = kitCap();
      if (studFollows && cap) {
        var match = studGroup.querySelector('[data-ez-stud-input][value="' + cap + '"]');
        if (match) match.checked = true;
      }
      if (!cap) {
        // no skirting color yet: nothing preselected, nothing priced
        studFollows = true;
        studGroup.querySelectorAll('[data-ez-stud-input]').forEach(function (inp) { inp.checked = false; });
      }
      studGroup.classList.toggle('is-waiting', !cap);
      var up = '+' + money(STUD_UPGRADE_CENTS);
      var capName = '';
      studGroup.querySelectorAll('[data-ez-stud-input]').forEach(function (inp) {
        var lab = inp.closest('.ez-pdp__swatch');
        if (lab) lab.classList.toggle('is-selected', inp.checked);
        if (inp.value === cap) capName = inp.getAttribute('data-color-name') || cap;
        // under each color: what picking it costs
        var tag = lab && lab.querySelector('[data-ez-stud-tag]');
        if (tag) {
          tag.textContent = !cap ? '' : (inp.value === cap ? 'Included' : up);
          tag.classList.toggle('is-included', !!cap && inp.value === cap);
          tag.classList.toggle('is-upgrade', !!cap && inp.value !== cap);
        }
      });
      var p = studPick();
      var diff = !!(p && cap && p.code !== cap);
      if (studNote) {
        if (diff) {
          studNote.innerHTML =
            '<p class="ez-pdp__stud-status"><span class="ez-stud-pill is-upgrade">' + escapeHtml(up) + '</span> <b>Stud color upgrade: ' + escapeHtml(p.name) + ' studs.</b></p>' +
            '<p>One flat ' + escapeHtml(up) + ' for the whole kit, not per snap. Caps stay ' + escapeHtml(capName) + ' to match your skirting. Pick ' + escapeHtml(capName) + ' again for included studs.</p>';
        } else if (!cap) {
          studNote.innerHTML =
            '<p class="ez-pdp__stud-status">Pick your skirting color first.</p>' +
            '<p>Studs that match it are <b>included</b>. Any other stud color is one flat <b>' + escapeHtml(up) + '</b> for the whole kit, not per snap.</p>';
        } else {
          studNote.innerHTML =
            '<p class="ez-pdp__stud-status"><span class="ez-stud-pill is-included">Included</span> <b>' + escapeHtml(capName) + ' studs</b> match your ' + escapeHtml(capName) + ' skirting. No extra cost.</p>' +
            '<p>Want studs that blend with your RV body or trim instead? Pick that color: one flat <b>' + escapeHtml(up) + '</b> for the whole kit, not per snap.</p>';
        }
      }
      if (boxSnaps) {
        boxSnaps.textContent = !cap ? boxSnapsOrig
          : diff ? boxSnapsN + ' snaps: ' + capName + ' caps & ' + p.name + ' studs, plus pins'
          : boxSnapsN + ' snaps: ' + capName + ' studs & caps, plus pins';
      }
    }
    function studUpgrade() {
      var p = studPick(), cap = kitCap();
      return (p && cap && p.code !== cap) ? p : null;
    }
    if (studGroup) {
      studGroup.addEventListener('change', function (e) {
        if (!e.target || !e.target.matches('[data-ez-stud-input]')) return;
        if (!kitCap()) {
          // tapped before the skirting color: don't pick, point at what's missing
          e.target.checked = false;
          flagNeeded();
          syncStud();
          return;
        }
        studFollows = e.target.value === kitCap();
        recompute(true);
      });
    }
    var summaryHost = root.querySelector('[data-ez-summary]');
    var totalEl = root.querySelector('[data-ez-total]');
    var qtyInput = root.querySelector('[data-ez-qty-input]');
    var summaryWrap = root.querySelector('[data-ez-summary-wrap]');
    var priceRow = root.querySelector('[data-ez-price-row]');

    // ── GATING (demo band buy card) ──────────────────────────────────────────
    // When the section opts in via data-require-selection, the buy card starts with NO option
    // preselected: the ATC reads "Pick height & color", the total block + resolved price are hidden,
    // and the sticky bar shows a disabled "Finish your build ↑" — until EVERY option group has a
    // pick (matches BandPDP's `coreResolved = !!height && !!color`).
    var requireSelection = root.getAttribute('data-require-selection') === 'true';
    var gatedAtcLabel = root.getAttribute('data-gated-label') || 'Pick your options';

    function allResolved() {
      if (!requireSelection) return true;
      for (var gi = 0; gi < optGroups.length; gi++) {
        if (!optGroups[gi].querySelector('[data-ez-opt-input]:checked')) return false;
      }
      return true;
    }

    // ── the sticky ATC bar bound to THIS section (data-atc-for === this section id) ──
    var bar = sectionId ? document.querySelector('[data-ez-atc-bar][data-atc-for="' + sectionId + '"]') : null;
    if (bar) bar.dataset.ezAtcClaimed = '1';
    var barTitleEl = bar && bar.querySelector('[data-ez-atc-title]');
    var barMetaEl = bar && bar.querySelector('[data-ez-atc-meta]');
    var barPriceEl = bar && bar.querySelector('[data-ez-atc-price]');
    var barBtn = bar && bar.querySelector('[data-ez-atc-btn]');
    var barCta = (barBtn && barBtn.getAttribute('data-cta')) || 'Add to cart →';
    var barGatedLabel = (barBtn && barBtn.getAttribute('data-disabled-label')) || 'Finish your build';
    var barMetaGated = bar && bar.getAttribute('data-meta-gated') || '';

    // ── extension modules (e.g. the roll snap-density mini-configurator) ──
    // A module registers { summary(ctx)->[{label,meta,price}], lines(ctx)->[{id,quantity}] }.
    // ctx = { qty, color } (color = the current colour-card/swatch value, upper/lower as authored).
    // Modules recompute their own price against the shared shade engine and feed rows+lines back
    // into the summary/total and the multi-line ATC — so the roll's tier picker never re-implements
    // the summary/ATC plumbing. registerModule() may request a recompute after it wires up.
    var modules = [];
    root.ezRegisterModule = function (m) { if (m) { modules.push(m); } };
    root.ezRecompute = function () { recompute(true); };

    function moduleCtx() {
      return { qty: qty(), color: currentColorValue() };
    }
    // the active colour-card / swatch value (drives the snap-pack colour). Falls back to '' when the
    // product has no colour axis.
    function currentColorValue() {
      var picked = root.querySelector('.ez-pdp__colour-card input:checked, .ez-pdp__swatch input:checked');
      return picked ? picked.value : '';
    }

    // picked value per option POSITION (data-ez-opt-index); an unpicked option is left out, so a shopper who
    // picks the color before the height still resolves a variant (the first height in that color)
    function currentOptions() {
      var want = {};
      optGroups.forEach(function (grp, gi) {
        var picked = grp.querySelector('[data-ez-opt-input]:checked');
        var at = parseInt(grp.getAttribute('data-ez-opt-index'), 10);
        if (picked) want[isNaN(at) ? gi : at] = picked.value;
      });
      return want;
    }

    function matchVariant() {
      var want = currentOptions();
      var keys = Object.keys(want);
      if (!keys.length) return sel.options[sel.selectedIndex];
      var opts = sel.options, i, o;
      for (i = 0; i < opts.length; i++) {
        o = opts[i];
        var parts = o.getAttribute('data-opts') != null
          ? o.getAttribute('data-opts').split('~~').map(function (p) { return p.trim(); })
          : o.textContent.split(' / ').map(function (p) { return p.trim(); });
        var ok = keys.every(function (k) { return parts[+k] === want[k]; });
        if (ok) return o;
      }
      return null;
    }

    // when the price element shows a min–max RANGE (demo band panel), keep the range until the
    // shopper actively picks an option.
    var priceIsRange = !!(priceEl && priceEl.getAttribute('data-ez-price-range'));
    var priceRangeAttr = priceEl && priceEl.getAttribute('data-ez-price-range');
    var priceRangeInner = priceEl ? priceEl.innerHTML : '';

    // ── the currently-selected kit variant (id, unit price in cents, availability, label) ──
    function currentKit() {
      var o = matchVariant();
      if (!o) return null;
      return {
        id: o.value,
        price: parseInt(o.getAttribute('data-price-cents'), 10) || 0,
        available: o.getAttribute('data-available') === 'true',
        label: o.getAttribute('data-kit-label') || '',
        sku: o.getAttribute('data-sku') || ''
      };
    }

    function qty() { return Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1); }

    // ── the resolved state of one add-on row (checked? which variant? price? multi-line combo?) ──
    // A row is one of:
    //   • single-variant  → data-ez-addon-vid + data-ez-addon-cents (optional colour sub-picker
    //                        overrides both), one item[] line;
    //   • ENGINE COMBO     → data-ez-addon-combo = { colorCode: [{id,qty,price},…] } (band body-color
    //                        studs: the studCombo pack breakdown, keyed by the sub-picker colour).
    //                        Checked → ALL of that colour's packs become item[] lines; price = their sum.
    function addonState(row) {
      var toggle = row.querySelector('[data-ez-addon-toggle]');
      var on = !!(toggle && toggle.checked);
      var perKit = row.getAttribute('data-ez-addon-perkit') === 'true';
      var comboAttr = row.getAttribute('data-ez-addon-combo');
      var label = row.getAttribute('data-ez-addon-label') || '';

      if (row.getAttribute('data-ez-addon-studswap') === 'true') {
        // stud color swap (2026-10-07): this row only picks the stud color for the kit's snap packs (added at
        // submit by EZKitSnaps). Matching the material = included; a different color = a flat $60 per build.
        var sPicked = row.querySelector('[data-ez-addon-variant]:checked');
        var sCode = (sPicked && sPicked.getAttribute('data-color')) || row.getAttribute('data-ez-addon-color') || '';
        var sKit = currentKit();
        var sCap = sKit && sKit.sku ? sKit.sku.split('-').pop() : '';
        var differs = on && sCode && sCap && sCode !== sCap;
        var sName = (sPicked && sPicked.getAttribute('data-color-name')) || sCode;
        return { on: on, studswap: true, stud: differs ? sCode : '', vid: '', price: differs ? 6000 : 0,
          perKit: false, lines: [], label: label, meta: differs ? (sName + ' studs') : 'Matches your skirting' };
      }

      if (comboAttr) {
        var combo;
        try { combo = JSON.parse(comboAttr); } catch (e) { combo = null; }
        // which colour is picked? (radio in the sub-picker). Fall back to the row's default colour.
        var picked = row.querySelector('[data-ez-addon-variant]:checked');
        var code = (picked && picked.getAttribute('data-color')) || row.getAttribute('data-ez-addon-color') || '';
        var packs = (combo && (combo[code] || combo[Object.keys(combo)[0]])) || [];
        var price = 0, lines = [];
        packs.forEach(function (p) {
          price += (parseInt(p.price, 10) || 0) * (p.qty || 1);
          lines.push({ id: String(p.id), quantity: p.qty || 1 });
        });
        var meta = row.getAttribute('data-ez-addon-meta') || '';
        var cname = (picked && picked.getAttribute('data-color-name')) || '';
        if (cname) meta = meta ? (meta + ' · ' + cname) : cname;
        return { on: on, vid: lines.length ? lines[0].id : '', price: price, perKit: perKit, lines: lines, label: label, meta: meta };
      }

      // a row may carry a colour sub-picker: the checked swatch input holds the active variant id/price
      var pick = row.querySelector('[data-ez-addon-variant]:checked');
      var vid = row.getAttribute('data-ez-addon-vid');
      var uprice = parseInt(row.getAttribute('data-ez-addon-cents'), 10) || 0;
      if (pick) {
        vid = pick.value;
        uprice = parseInt(pick.getAttribute('data-price-cents'), 10) || uprice;
      }
      return {
        on: on, vid: vid, price: uprice, perKit: perKit,
        label: label,
        meta: row.getAttribute('data-ez-addon-meta') || ''
      };
    }

    // ── recompute the line-item summary + total, sync ATC label ──
    function recompute(userInitiated) {
      var kit = currentKit();
      if (!kit) return;
      sel.value = kit.id;

      // kit unit price shown at the top of the card (unless a range is holding, or the card is
      // still gated with no resolved selection — then keep the from–to range visible)
      var gatedHold = requireSelection && !allResolved();
      if (priceEl && !gatedHold && (!priceIsRange || userInitiated)) {
        priceEl.textContent = money(kit.price);
      }

      // opt/swatch selected-state
      root.querySelectorAll('[data-ez-opt-input]').forEach(function (inp) {
        var lab = inp.closest('label');
        if (lab) lab.classList.toggle('is-selected', inp.checked);
      });
      // add-on selected-state (row highlight + swatch sub-picker)
      addonRows.forEach(function (row) {
        var t = row.querySelector('[data-ez-addon-toggle]');
        row.classList.toggle('is-on', !!(t && t.checked));
        row.querySelectorAll('[data-ez-addon-variant]').forEach(function (inp) {
          var lab = inp.closest('label');
          if (lab) lab.classList.toggle('is-selected', inp.checked);
        });
      });

      var q = qty();
      var total = kit.price * q;
      // kit row: the card/product name + (variant · ×qty) meta, mirroring the demo summary line
      var kitName = root.getAttribute('data-kit-summary') || root.getAttribute('data-kit-name') || 'This item';
      // the demo summary line attaches the line quantity to the name ("Precut shade ×1 · Black");
      // an optional data-kit-summary-detail (e.g. the precut size "24×48") slots before the variant.
      var kitDetail = root.getAttribute('data-kit-summary-detail') || '';
      var kitMeta = [];
      if (kitDetail) kitMeta.push(kitDetail);
      if (kit.label) kitMeta.push(kit.label);
      var rows = [{ label: kitName + ' ×' + q, meta: kitMeta.join(' · '), price: kit.price * q }];
      // extension-module summary rows (e.g. the roll snap tier) — inserted BEFORE add-ons so the
      // "roll + snaps" reads as the kit, then optional add-ons follow (matches the demo order).
      // A module may also drive the HEADLINE price (headlineAdd = cents to add to the top price) so
      // the roll's big number reads as the composed kit price (roll + snaps), like the demo.
      var ctx = moduleCtx();
      var headlineAdd = 0;
      modules.forEach(function (m) {
        if (typeof m.summary === 'function') {
          (m.summary(ctx) || []).forEach(function (r) {
            if (r && r.price != null) { total += r.price; rows.push({ label: r.label, meta: r.meta, price: r.price }); }
          });
        }
        if (typeof m.headlineAdd === 'function') { headlineAdd += (m.headlineAdd(ctx) || 0); }
      });
      // re-apply the composed headline price (bare variant + module headline add) once modules ran.
      if (priceEl && (!priceIsRange || userInitiated) && headlineAdd > 0) {
        priceEl.textContent = money(kit.price + headlineAdd);
      }
      // stud color upgrade: one flat charge per kit build when the studs differ from the material color
      syncStud();
      var sUp = studUpgrade();
      if (sUp) {
        total += STUD_UPGRADE_CENTS;
        rows.push({ label: 'Stud color upgrade', meta: sUp.name + ' studs', price: STUD_UPGRADE_CENTS });
      } else if (studGroup && kitCap()) {
        var sm = studPick();
        rows.push({ label: 'Stud color', meta: (sm ? sm.name : '') + ', matches your skirting', price: 0, priceText: 'Included' });
      }
      addonRows.forEach(function (row) {
        var st = addonState(row);
        if (!st.on || !st.vid) return;
        var lineQty = st.perKit ? q : 1;
        var lineTotal = st.price * lineQty;
        total += lineTotal;
        rows.push({ label: st.label, meta: st.meta, price: lineTotal });
      });

      // render summary
      if (summaryHost) {
        var html = '';
        rows.forEach(function (r) {
          html += '<div class="ez-pdp__sum-row"><span class="ez-pdp__sum-label">' + escapeHtml(r.label) +
            (r.meta ? ' <span class="ez-pdp__sum-meta">· ' + escapeHtml(r.meta) + '</span>' : '') +
            '</span><span class="ez-pdp__sum-price">' + (r.priceText ? escapeHtml(r.priceText) : money(r.price)) + '</span></div>';
        });
        summaryHost.innerHTML = html;
      }
      if (totalEl) totalEl.textContent = money(total);

      // ── gate state: hide the total block + resolved price until every option resolves ──
      var resolved = allResolved();
      if (requireSelection) {
        if (summaryWrap) summaryWrap.hidden = !resolved;
        if (priceRow) priceRow.classList.toggle('is-gated', !resolved);
        // hold the min–max range on the price line while gated (demo shows "Standard · Tall" spans)
        if (priceEl && priceRangeAttr && !resolved) {
          priceEl.innerHTML = priceRangeInner;
        }
      }

      // ATC label + availability
      if (atc) {
        var gated = requireSelection && !resolved;
        atc.disabled = gated || !kit.available;
        atc.classList.toggle('is-gated', gated);
        if (atcText) {
          if (gated) {
            atcText.textContent = gatedAtcLabel;
          } else if (kit.available) {
            atcText.innerHTML = escapeHtml(atc.getAttribute('data-label-add')) + ' — ' + money(total) + ' →';
          } else {
            atcText.textContent = atc.getAttribute('data-label-soldout');
          }
        }
      }

      // ── mirror everything to the sticky ATC bar (bound title/meta/price + gated button) ──
      if (bar) {
        var barGated = requireSelection && !resolved;
        if (barTitleEl && root.getAttribute('data-atc-title')) barTitleEl.textContent = root.getAttribute('data-atc-title');
        if (barMetaEl) {
          barMetaEl.textContent = barGated ? barMetaGated
            : buildBarMeta(kit, rows.length - 1); // rows beyond the kit = add-ons/module lines
        }
        if (barPriceEl) barPriceEl.textContent = barGated ? '' : money(total);
        if (barBtn) {
          barBtn.classList.toggle('is-disabled', barGated || !kit.available);
          barBtn.textContent = barGated ? (barGatedLabel + ' ↑')
            : (kit.available ? barCta : (atc && atc.getAttribute('data-label-soldout')) || 'Sold out');
        }
      }
    }

    // sticky-bar meta line: prefer the current variant label, append "+ add-ons" when extras chosen
    function buildBarMeta(kit, extraCount) {
      var base = (kit && kit.label) || '';
      if (extraCount > 0) base += (base ? ' · ' : '') + '+ add-ons';
      return base;
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
      });
    }

    // "Pick your height first": the first unpicked option group gets a copper label + one-line hint while any
    // later choice has been touched; it clears the moment that group is picked
    function groupName(grp) {
      var l = grp.querySelector('.ez-pdp__opt-label');
      var t = l ? l.textContent.replace(/^\s*\d+\s*·\s*/, '').trim() : '';
      return t ? t.toLowerCase() : 'option';
    }
    function flagNeeded(upTo) {
      var first = null;
      optGroups.forEach(function (grp, gi) {
        if (upTo != null && gi >= upTo) return;
        if (!first && !grp.querySelector('[data-ez-opt-input]:checked')) first = grp;
      });
      optGroups.forEach(function (grp) {
        var on = grp === first;
        grp.classList.toggle('is-needed', on);
        var hint = grp.querySelector('[data-ez-opt-need]');
        if (on && !hint) {
          hint = document.createElement('div');
          hint.className = 'ez-pdp__opt-need';
          hint.setAttribute('data-ez-opt-need', '');
          hint.setAttribute('role', 'status');
          var lab = grp.querySelector('.ez-pdp__opt-label');
          if (lab && lab.nextSibling) grp.insertBefore(hint, lab.nextSibling); else grp.appendChild(hint);
        }
        if (hint) {
          hint.textContent = on ? 'Pick your ' + groupName(grp) + ' first.' : '';
          hint.hidden = !on;
        }
      });
    }
    optInputs.forEach(function (inp) {
      inp.addEventListener('change', function () {
        var grp = inp.closest('[data-ez-opt-index]');
        flagNeeded(optGroups.indexOf(grp));
        recompute(true);
      });
    });
    addonRows.forEach(function (row) {
      row.querySelectorAll('[data-ez-addon-toggle], [data-ez-addon-variant]').forEach(function (inp) {
        inp.addEventListener('change', function () { recompute(true); });
      });
    });

    // quantity steppers
    root.querySelectorAll('[data-ez-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var v = parseInt(qtyInput.value, 10) || 1;
        v += parseInt(btn.getAttribute('data-ez-qty'), 10);
        qtyInput.value = v < 1 ? 1 : v;
        recompute(true);
      });
    });
    if (qtyInput) qtyInput.addEventListener('change', function () { recompute(true); });

    // ── multi-line add-to-cart: kit variant + every checked add-on → POST /cart/add.js items[] ──
    var form = root.querySelector('form[action*="/cart/add"], #ez-pdp-form');
    var redirect = root.getAttribute('data-cart-redirect') === 'true';
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (atc && atc.disabled) return;
        var kit = currentKit();
        if (!kit) { form.submit(); return; }
        var q = qty();
        var items = [{ id: Number(kit.id), quantity: q }];
        // extension-module lines (snap packs for the chosen roll tier, etc.) — each { id, quantity }
        var mctx = moduleCtx();
        modules.forEach(function (m) {
          if (typeof m.lines !== 'function') return;
          (m.lines(mctx) || []).forEach(function (ln) {
            if (ln && ln.id) items.push({ id: Number(ln.id), quantity: ln.quantity || 1 });
          });
        });
        addonRows.forEach(function (row) {
          var st = addonState(row);
          if (!st.on) return;
          if (st.lines && st.lines.length) {
            // engine combo (band body-color studs): push EVERY pack line for the chosen colour
            st.lines.forEach(function (ln) {
              if (ln && ln.id) items.push({ id: Number(ln.id), quantity: (ln.quantity || 1) * (st.perKit ? q : 1) });
            });
          } else if (st.vid) {
            items.push({ id: Number(st.vid), quantity: st.perKit ? q : 1 });
          }
        });

        // tag every line of this build so the cart groups it into its own section (hidden _ properties)
        var bid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        var bprops = { _build: bid, _build_kind: 'pdp', _build_label: (kit.label || 'Your order') };
        items = items.map(function (it) { return { id: it.id, quantity: it.quantity, properties: bprops }; });

        // stud color swap (2026-10-07): kits no longer carry their snap pack in the warehouse BOM, so every kit
        // add-to-cart also adds its packs as EZ-C-SNA-{n}-{material}-{stud} (sizes from data-kit-snap-packs, the
        // page's existing split). All-or-nothing: if the packs can't be added, nothing is added.
        var packsAttr = root.getAttribute('data-kit-snap-packs');
        if (packsAttr && /^EZ-B-(SKT|SKS|HIT|SHP)-/.test(kit.sku || '')) {
          var cap = kit.sku.split('-').pop();
          var stud = cap;
          var sPick = studPick();
          if (sPick && sPick.code) stud = sPick.code;
          var kitFail = function () {
            if (atc) atc.disabled = false;
            window.alert('Sorry, we could not add this kit right now. Please try again, or call us at 1-877-439-7627.');
          };
          if (!window.EZKitSnaps) { kitFail(); return; }
          var packs;
          try { packs = JSON.parse(packsAttr); } catch (err) { kitFail(); return; }
          if (atc) atc.disabled = true;
          window.EZKitSnaps.lines({ packs: packs, cap: cap, stud: stud, kitQty: q, props: bprops })
            .then(function (snapLines) {
              if (!snapLines.length) throw new Error('no snap lines');
              var all = items.concat(snapLines);
              if (redirect) { postItems(all, function () { window.location.href = '/cart'; }, kitFail); return; }
              postItems(all, function (res) {
                document.dispatchEvent(new CustomEvent('ez:cart:added', { detail: { items: (res && res.items) || res } }));
                if (atc) atc.disabled = false;
                recompute(true);
              }, kitFail);
            })
            .catch(kitFail);
          return;
        }

        if (redirect) {
          // build a native multi-line POST is awkward; use the AJAX add then hard-redirect.
          postItems(items, function () { window.location.href = '/cart'; }, function () { form.submit(); });
          return;
        }
        if (atc) atc.disabled = true;
        postItems(items, function (res) {
          document.dispatchEvent(new CustomEvent('ez:cart:added', { detail: { items: (res && res.items) || res } }));
          if (atc) atc.disabled = false;
          recompute(true);
        }, function () { form.submit(); });
      });
    }

    function postItems(items, ok, fail) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(ok)
        .catch(fail);
    }

    // ── sticky ATC bar: show once the buy panel scrolls above the fixed nav (rect.bottom < 80),
    //    matching the demo StickyAtc sentinel. The bar's button either triggers the same multi-line
    //    add (form submit path) or, while gated, scrolls back to the buy panel. rAF-throttled. ──
    if (bar) {
      var sentinel = root.querySelector('.ez-pdp__buy') || root;
      var footEl = document.querySelector('.ez-footer') || document.querySelector('footer');
      var raf = 0;
      var syncBar = function () {
        raf = 0;
        var r = sentinel.getBoundingClientRect();
        var show = r.bottom < 80;
        // hide once the footer scrolls into view, so the fixed bar never covers the footer's last line (© / patent)
        if (show && footEl && footEl.getBoundingClientRect().top < window.innerHeight - 8) show = false;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        // a11y: an aria-hidden bar must not hold a focusable button; inert removes it from the tab order until shown
        bar.inert = !show;
      };
      var onScroll = function () { if (!raf) raf = requestAnimationFrame(syncBar); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      syncBar();

      if (barBtn) {
        barBtn.addEventListener('click', function () {
          if (requireSelection && !allResolved()) {
            // gated: scroll back to the buy panel so the shopper can finish (demo behaviour)
            (root.querySelector('.ez-pdp__buy') || root).scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
          if (form && form.requestSubmit) { form.requestSubmit(); }
          else if (form) { form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); }
        });
      }
    }

    recompute(false);
  }

  function initAll() {
    document.querySelectorAll('.ez-pdp').forEach(initInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    (target.querySelectorAll ? target.querySelectorAll('.ez-pdp') : []).forEach(initInstance);
  });
})();
